export const SUMMARY_LINE_SLOT_PX = 6;
export const SUMMARY_ROW_MIN_PX = 30;
export const SUMMARY_ROW_GUTTER_PX = 6;
export const FULL_EVENT_SLOT_PX = 46;
export const FULL_EVENT_ROW_MIN_PX = 52;
export const FULL_EVENT_ROW_GUTTER_PX = 16;

/** Normalize Firestore / legacy values to Date for MobiScroll (expects Date, not Timestamp). */
export const normalizeCalendarDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value._seconds) {
    const d = new Date(
      value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1e6)
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toTime = (value) => {
  const d = normalizeCalendarDate(value);
  return d ? d.getTime() : NaN;
};

/** Greedy lane assignment for overlapping events (same algorithm as timeline stacking). */
export const assignSummaryLanes = (
  events,
  slotPx = SUMMARY_LINE_SLOT_PX,
  gutterPx = SUMMARY_ROW_GUTTER_PX,
  minPx = SUMMARY_ROW_MIN_PX
) => {
  if (!events.length) {
    return { events: [], maxLane: -1, rowHeightPx: minPx };
  }

  const sorted = [...events].sort((a, b) => toTime(a.start) - toTime(b.start));
  const laneEnds = [];
  let maxLane = 0;

  const withLanes = sorted.map((ev) => {
    const start = toTime(ev.start);
    const end = toTime(ev.end);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      return { ...ev, summaryLane: 0 };
    }

    let lane = laneEnds.findIndex((laneEnd) => laneEnd < start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = Math.max(laneEnds[lane], end);
    }
    maxLane = Math.max(maxLane, lane);
    return { ...ev, summaryLane: lane };
  });

  const rowHeightPx = Math.max(
    minPx,
    (maxLane + 1) * slotPx + gutterPx
  );

  return { events: withLanes, maxLane, rowHeightPx };
};

const eventRange = (ev) => {
  const isRentman = !!ev.rentmanEvent;
  const start = normalizeCalendarDate(isRentman ? ev.planStart : ev.start);
  const end = normalizeCalendarDate(isRentman ? ev.planEnd : ev.end);
  if (!start || !end || start.getTime() > end.getTime()) return null;
  return { start, end };
};

/** Peak stack depth per resource for full-size child-row events. */
export const computeResourceStackHeights = (events) => {
  const byResource = new Map();

  (events || []).forEach((ev) => {
    if (ev?.isSummaryLine) return;
    const range = eventRange(ev);
    if (!range) return;

    const resourceIds = Array.isArray(ev.resource) ? ev.resource : [ev.resource];
    resourceIds.forEach((rid) => {
      if (rid == null) return;
      const key = String(rid);
      if (!byResource.has(key)) byResource.set(key, []);
      byResource.get(key).push(range);
    });
  });

  const heights = new Map();
  byResource.forEach((ranges, resourceId) => {
    const { rowHeightPx } = assignSummaryLanes(
      ranges,
      FULL_EVENT_SLOT_PX,
      FULL_EVENT_ROW_GUTTER_PX,
      FULL_EVENT_ROW_MIN_PX
    );
    heights.set(resourceId, rowHeightPx);
  });
  return heights;
};

export const flattenTimelineResources = (resources) => {
  const flat = [];
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      flat.push(node);
      if (node.children?.length && node.collapsed !== true) {
        walk(node.children);
      }
    });
  };
  walk(resources);
  return flat;
};

export const collectParentResourceIds = (resources) => {
  const ids = [];
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      if (node.children?.length) {
        ids.push(node.id);
        walk(node.children);
      }
    });
  };
  walk(resources);
  return ids;
};

/** All group headers (including nested Rentman/Freelancers) -> Set of descendant resource ids. */
export const buildParentDescendantMap = (resources) => {
  const map = new Map();

  const collectDescendantIds = (resource, ids = new Set()) => {
    resource.children?.forEach((child) => {
      ids.add(String(child.id));
      collectDescendantIds(child, ids);
    });
    return ids;
  };

  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node) => {
      if (node.children?.length) {
        map.set(String(node.id), collectDescendantIds(node));
        walk(node.children);
      }
    });
  };

  walk(resources);
  return map;
};

/**
 * Apply computed row heights by resource id (robust — never relies on the map
 * key/DOM-index alignment). Handles both parent summary rows and leaf event rows.
 * MobiScroll does not always grow leaf rows for stacked all-day/invalid resources
 * (e.g. the Rentman child row), so we set min-height explicitly.
 */
export const applyTimelineRowHeights = (rowHeightsById) => {
  if (typeof document === 'undefined' || !rowHeightsById?.size) return;

  const root = document.querySelector('.motion-air-calendar');
  if (!root) return;

  const sidebarRows = root.querySelectorAll(
    '.mbsc-timeline-resource-col .mbsc-timeline-resource.mbsc-timeline-row'
  );
  const gridRows = root.querySelectorAll('.mbsc-timeline-grid .mbsc-timeline-row');

  sidebarRows.forEach((sidebarRow, index) => {
    const resourceId = sidebarRow.querySelector('[data-resource-id]')?.dataset.resourceId;
    if (!resourceId) return;

    const height = rowHeightsById.get(String(resourceId));
    if (!height) return;

    const px = `${height}px`;
    sidebarRow.style.minHeight = px;

    const gridRow = gridRows[index];
    if (gridRow) {
      gridRow.style.minHeight = px;
    }
  });
};

/** Position summary lines at 6px lane pitch (MobiScroll default lane pitch is ~46px). */
export const applySummaryLineLanePositions = () => {
  if (typeof document === 'undefined') return;

  document
    .querySelectorAll('.motion-air-calendar .mbsc-schedule-event.summary-line-event')
    .forEach((el) => {
      const match = el.className.match(/summary-lane-(\d+)/);
      if (!match) return;
      el.style.setProperty(
        'top',
        `${parseInt(match[1], 10) * SUMMARY_LINE_SLOT_PX}px`,
        'important'
      );
    });
};

export const applyParentSummaryLayoutToResources = (resources, rowHeightsById) => {
  if (!Array.isArray(resources)) return [];

  return resources.map((node) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    if (!hasChildren) return node;

    const rowHeightPx = rowHeightsById.get(String(node.id));

    return {
      ...node,
      ...(rowHeightPx ? { summaryRowMinHeight: rowHeightPx } : {}),
      children: applyParentSummaryLayoutToResources(node.children, rowHeightsById),
    };
  });
};
